import React, { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Textarea } from 'components/ui/textarea'
import { Label } from 'components/ui/label'
import { Image, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from 'lib/utils'
import { ImageLightbox } from 'components/ui/image-lightbox'
import ImageDeleteConfirm from './ImageDeleteConfirm'
import { transformUrl, TRANSFORM } from 'components/FileUploader/fileUploadUtils'

interface ImageData {
	_id: string
	url: string
	note: string
	type: string
	[key: string]: any
}

interface ImageReviewPopupProps {
	open: boolean
	onClose: () => void
	image: ImageData | null
	onUpdateNote: (updatedImage: ImageData) => void
	onDeleteImage: (image: ImageData) => void
	/** When true, the Remove Image button is rendered disabled. Used by callers that
	 *  surface images they don't own (e.g., Portfolio Images shows location-project
	 *  images that should be deleted from the location's own page, not from here). */
	disableDelete?: boolean
	/** Tooltip shown on the disabled Remove Image button. */
	disableDeleteTitle?: string
	/** When true, the note is read-only and Update Note is disabled. */
	disableUpdate?: boolean
	/** Tooltip shown on the disabled Update Note button. */
	disableUpdateTitle?: string
	/**
	 * The whole set the user is browsing, enabling prev/next inside the dialog.
	 * Optional: callers that review a single image keep passing only `image`.
	 * When supplied, `image` selects which one opens first.
	 */
	images?: ImageData[]
}

const ImageReviewPopup: React.FC<ImageReviewPopupProps> = ({
	open,
	onClose,
	image,
	onUpdateNote,
	onDeleteImage,
	disableDelete = false,
	disableDeleteTitle,
	disableUpdate = false,
	disableUpdateTitle,
	images,
}) => {
	const [note, setNote] = useState('')
	const [lightboxOpen, setLightboxOpen] = useState(false)
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
	const [index, setIndex] = useState(0)

	// Falls back to the single image so the cycling and non-cycling callers
	// share one code path below.
	const list = useMemo<ImageData[]>(
		() => (images && images.length > 0 ? images : image ? [image] : []),
		[images, image],
	)

	// `image` names which of `list` to open on. Keyed on the image id rather
	// than on `list` or `image` identity: a parent that rebuilds either on
	// every render would otherwise reset the index mid-cycle, undoing the
	// user's prev/next clicks.
	const targetId = image?._id
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const listIds = useMemo(() => list.map(item => item._id).join('|'), [list])

	useEffect(() => {
		if (!open || !targetId) return
		const found = list.findIndex(candidate => candidate._id === targetId)
		setIndex(found >= 0 ? found : 0)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, targetId, listIds])

	const current = list[index] ?? image ?? null

	useEffect(() => {
		setNote(current?.note || '')
	}, [current])

	const handleUpdateNote = () => {
		if (current) onUpdateNote({ ...current, note })
	}

	const handleConfirmDelete = () => {
		setDeleteConfirmOpen(false)
		if (current) onDeleteImage(current)
		onClose()
	}

	const canCycle = list.length > 1
	// Wraps, so cycling through a note's images never dead-ends.
	const step = (delta: number) => setIndex(prev => (prev + delta + list.length) % list.length)

	if (!current) return null

	return (
		<>
			<Dialog open={open} onClose={onClose}>
				<DialogContent className="max-w-lg p-0 overflow-hidden">
					<DialogHeader className="px-5 pt-5 pb-0">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<Image className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Review Image</DialogTitle>
								<DialogDescription>
									{canCycle
										? `Image ${index + 1} of ${list.length} — preview, annotate, or remove`
										: 'Preview, annotate, or remove this image'}
								</DialogDescription>
							</div>
						</div>
					</DialogHeader>

					{/* Image, with prev/next when reviewing a set */}
					<div className="relative">
						<div className="cursor-pointer" onClick={() => setLightboxOpen(true)}>
							<img
								src={transformUrl(current.url, TRANSFORM.PREVIEW)}
								alt={current.note || 'Image preview'}
								title="Click to zoom"
								className="w-full h-auto max-h-80 object-cover hover:opacity-90 transition-opacity"
							/>
						</div>

						{canCycle && (
							<>
								<button
									type="button"
									onClick={() => step(-1)}
									aria-label="Previous image"
									className={cn(
										'absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-1.5',
										'text-white hover:bg-black/70',
									)}
								>
									<ChevronLeft className="w-5 h-5" />
								</button>
								<button
									type="button"
									onClick={() => step(1)}
									aria-label="Next image"
									className={cn(
										'absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-1.5',
										'text-white hover:bg-black/70',
									)}
								>
									<ChevronRight className="w-5 h-5" />
								</button>
							</>
						)}
					</div>

					{/* Note input */}
					<div className="px-5 py-4">
						<Label htmlFor="image-note" className="mb-1.5">
							Note
						</Label>
						<Textarea
							id="image-note"
							value={note}
							onChange={e => setNote(e.target.value)}
							rows={2}
							disabled={disableUpdate}
							title={disableUpdate ? disableUpdateTitle : undefined}
							placeholder={
								disableUpdate ? 'Only the uploader can edit this note' : 'Add a note for this image...'
							}
						/>
					</div>

					{/* Action buttons */}
					<div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
						<Button
							variant="danger"
							onClick={() => setDeleteConfirmOpen(true)}
							disabled={disableDelete}
							title={disableDelete ? disableDeleteTitle : undefined}
						>
							Remove Image
						</Button>
						<Button
							variant="primary"
							onClick={handleUpdateNote}
							disabled={disableUpdate}
							title={disableUpdate ? disableUpdateTitle : undefined}
						>
							Update Note
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<ImageLightbox
				src={current.url}
				alt={current.note || 'Image preview'}
				open={lightboxOpen}
				onClose={() => setLightboxOpen(false)}
			/>

			<ImageDeleteConfirm
				open={deleteConfirmOpen}
				onClose={() => setDeleteConfirmOpen(false)}
				onConfirm={handleConfirmDelete}
			/>
		</>
	)
}

export default ImageReviewPopup
